import React, { useState, useEffect } from 'react';
import { X, UserPlus, FileText, User, MapPin, Edit2, Save, Check } from 'lucide-react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore'; // r1-p1-docterm-rdm-f7c2e91a
import { db } from '../../firebase/config';
import { DOC_TYPES, resolveDocTerminology } from '../../utils/quoteSchema'; // r1-p1-docterm-rdm-f7c2e91a
import { canSeeAllCustomers } from '../../utils/permissions'; // s9office-6e2a9d53

const RequestDetailModal = ({ 
    request, 
    onClose, 
    onConvertCustomer, 
    onCreateQuote, 
    isConverting,
    users = [],
    currentUserProfile,
    onUpdate,
    onBookNow,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Book Now flow: 'idle' | 'confirming' | 'booking' | 'booked' | 'booked_no_email' | 'error'
    const [bookingState, setBookingState] = useState('idle');
    const [bookingError, setBookingError] = useState('');
    const [bookedResult, setBookedResult] = useState(null);
    
    // Form State
    const [formData, setFormData] = useState({});

    // r1-p1-docterm-rdm-f7c2e91a
    // Org-default doc terminology for this lead-side modal. A lead has no frozen docType
    // (the document doesn't exist until "Create"/"Book Now"), so every label here reads the
    // org default — the kind of document about to be created. Self-contained org-doc listener
    // (companySettings isn't passed to this modal); missing/loading → DOC_TYPES.QUOTE → "Quote".
    const [quoteTerminologyDefault, setQuoteTerminologyDefault] = useState(DOC_TYPES.QUOTE);
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId) return;
        const unsub = onSnapshot(doc(db, `organizations/${orgId}`), (d) => {
            if (d.exists()) setQuoteTerminologyDefault(d.data()?.quoteTerminologyDefault || DOC_TYPES.QUOTE);
        });
        return () => unsub();
    }, [currentUserProfile?.organizationId]);
    const { word: docWord, wordPlural: docWordPlural } = resolveDocTerminology(quoteTerminologyDefault);

    // Reset form when request changes
    useEffect(() => {
        if (request) {
            setFormData({
                customerName: request.customerName || '',
                customerEmail: request.customerEmail || '',
                customerPhone: request.customerPhone || '',
                source: request.source || 'Manual Entry',
                assignedTo: request.assignedTo || '',
                subject: request.subject || '',
                description: request.description || '',
                customerAddress: request.customerAddress || '',
                customerCity: request.customerCity || '',
                customerState: request.customerState || '',
                customerZip: request.customerZip || ''
            });
        }
    }, [request]);

    if (!request) return null;

    const isCustomerLinked = !!request.customerId;
    // s9office-6e2a9d53 — S9b (founder decision 2026-10-04): Office Staff edit and reassign any
    // request; firestore.rules requests update now allows isOffice() to reassign too.
    const canEdit = canSeeAllCustomers(currentUserProfile);

    // --- HANDLERS ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // 1. Determine Assigned Name if ID changed
            let updates = { ...formData };
            if (formData.assignedTo) {
                const assignedUser = users.find(u => u.id === formData.assignedTo);
                updates.assignedToName = assignedUser ? assignedUser.name : '';
            } else {
                updates.assignedToName = null;
            }

            // 2. Update Firestore
            const requestRef = doc(db, `organizations/${currentUserProfile.organizationId}/requests`, request.id);
            await updateDoc(requestRef, updates);

            // 3. Update Local State (via Parent Callback)
            if (onUpdate) onUpdate(updates);

            setIsEditing(false);
        } catch (error) {
            console.error("Error updating request:", error);
            alert("Failed to save changes.");
        } finally {
            setIsSaving(false);
        }
    };

    // --- RENDER HELPERS ---
    const fullAddress = [
        formData.customerAddress,
        formData.customerCity,
        formData.customerState,
        formData.customerZip
    ].filter(Boolean).join(', ');

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] flex flex-col">
                
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100 flex-shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            {isEditing ? 'Edit Lead Details' : 'Lead Details'}
                        </h2>
                        <p className="text-sm text-gray-500">
                            Received on {request.createdAt?.toLocaleDateString()}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canEdit && !isEditing && (
                            <button 
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                            >
                                <Edit2 size={16} /> Edit
                            </button>
                        )}
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Scrollable Body */}
                <div className="p-6 space-y-6 overflow-y-auto">
                    
                    {/* Contact Info Panel */}
                    <div className={`p-4 rounded-lg border transition-colors ${isEditing ? 'bg-blue-50/50 border-blue-100' : 'bg-gray-50 border-gray-100'}`}>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3 flex items-center gap-2">
                            <User size={16} /> Contact Information
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">
                            {/* NAME */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Name</label>
                                {isEditing ? (
                                    <input name="customerName" value={formData.customerName} onChange={handleChange} className="w-full p-1.5 text-sm border rounded" />
                                ) : (
                                    <p className="font-medium text-gray-900">{formData.customerName}</p>
                                )}
                            </div>

                            {/* EMAIL */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Email</label>
                                {isEditing ? (
                                    <input name="customerEmail" value={formData.customerEmail} onChange={handleChange} className="w-full p-1.5 text-sm border rounded" />
                                ) : (
                                    <p className="font-medium text-gray-900 break-all">{formData.customerEmail}</p>
                                )}
                            </div>

                            {/* PHONE */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Phone</label>
                                {isEditing ? (
                                    <input name="customerPhone" value={formData.customerPhone} onChange={handleChange} className="w-full p-1.5 text-sm border rounded" />
                                ) : (
                                    <p className="font-medium text-gray-900">{formData.customerPhone || 'N/A'}</p>
                                )}
                            </div>

                            {/* SOURCE */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Source</label>
                                {isEditing ? (
                                    <select name="source" value={formData.source} onChange={handleChange} className="w-full p-1.5 text-sm border rounded bg-white">
                                        <option value="Phone Inquiry">Phone Inquiry</option>
                                        <option value="Walk-in">Walk-in</option>
                                        <option value="Referral">Referral</option>
                                        <option value="Email">Email</option>
                                        <option value="Social Media">Social Media</option>
                                        <option value="Web Search">Web Search</option>
                                        <option value="Advertisement">Advertisement</option>
                                        <option value="Manual Entry">Manual Entry</option>
                                        <option value="Other">Other</option>
                                    </select>
                                ) : (
                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                        {formData.source}
                                    </span>
                                )}
                            </div>

                            {/* ASSIGNED TO */}
                            <div className="col-span-1 md:col-span-2 border-t border-gray-200/50 pt-3 mt-1">
                                <label className="text-xs text-gray-500 mb-1 block">Assigned Sales Rep</label>
                                {isEditing ? (
                                    <select name="assignedTo" value={formData.assignedTo} onChange={handleChange} className="w-full p-1.5 text-sm border rounded bg-white font-medium text-blue-700">
                                        <option value="">-- Unassigned --</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        {formData.assignedTo ? (
                                            <span className="font-bold text-blue-700 bg-blue-50 px-2 py-1 rounded text-sm">
                                                {users.find(u => u.id === formData.assignedTo)?.name || 'Unknown User'}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 italic text-sm">Unassigned</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Message / Description Section */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">Request Details</h3>
                        <div className={`border rounded-lg p-4 ${isEditing ? 'bg-white border-blue-300 ring-2 ring-blue-50' : 'bg-white border-gray-200'}`}>
                            {isEditing ? (
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs text-gray-500">Subject</label>
                                        <input name="subject" value={formData.subject} onChange={handleChange} className="w-full p-2 border rounded font-bold text-gray-900" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500">Description</label>
                                        <textarea name="description" value={formData.description} onChange={handleChange} rows={4} className="w-full p-2 border rounded text-gray-600" />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <h4 className="font-bold text-gray-900 mb-2">{formData.subject}</h4>
                                    <p className="text-gray-600 whitespace-pre-wrap">{formData.description}</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="bg-gray-50 p-6 border-t border-gray-100 flex-shrink-0">

                    {/* ── Book Now: Confirmation Panel ── */}
                    {bookingState === 'confirming' && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                            <p className="text-sm font-semibold text-blue-900 mb-1">Confirm Booking</p>
                            <p className="text-sm text-blue-700 mb-1">
                                <strong>Customer:</strong> {formData.customerName}
                            </p>
                            <p className="text-sm text-blue-700 mb-1">
                                <strong>Service:</strong> {request.templateName || request.subject}
                            </p>
                            <p className="text-sm text-blue-700 mb-3">
                                <strong>{docWord} will be emailed to:</strong> {formData.customerEmail}
                            </p>
                            <p className="text-xs text-blue-600 mb-3">
                                A pending job will be added to your pipeline. You'll set the final date when scheduling.
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={async () => {
                                        setBookingState('booking');
                                        setBookingError('');
                                        try {
                                            const result = await onBookNow(request);
                                            if (result?.emailSent === false) {
                                                setBookedResult(result);
                                                setBookingState('booked_no_email');
                                            } else {
                                                setBookedResult(result);
                                                setBookingState('booked');
                                            }
                                        } catch (err) {
                                            console.error('Book Now error:', err);
                                            setBookingError(err.message === 'CUSTOMER_NOT_FOUND'
                                                ? 'Customer not found. Promote this lead first.'
                                                : err.message === 'NO_TEMPLATE'
                                                ? `No service template on this lead. Use Create ${docWord} instead.`
                                                : 'Something went wrong. Please try again.');
                                            setBookingState('error');
                                        }
                                    }}
                                    className="flex-1 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Confirm & Send {docWord}
                                </button>
                                <button
                                    onClick={() => setBookingState('idle')}
                                    className="px-4 py-2 bg-white border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Book Now: Booking in progress ── */}
                    {bookingState === 'booking' && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            <p className="text-sm text-blue-800 font-medium">Creating {docWord.toLowerCase()} and job…</p>
                        </div>
                    )}

                    {/* ── Book Now: Success ── */}
                    {(bookingState === 'booked' || bookingState === 'booked_no_email') && (
                        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                            <p className="text-sm font-bold text-green-800 mb-1">✓ Booking created!</p>
                            {bookingState === 'booked' ? (
                                <p className="text-sm text-green-700">{docWord} sent to <strong>{bookedResult?.customerEmail}</strong>. Pending job added to your pipeline.</p>
                            ) : (
                                <p className="text-sm text-green-700">Pending job created. {docWord} was saved but the email failed — resend from the {docWordPlural} page.</p>
                            )}
                        </div>
                    )}

                    {/* ── Book Now: Error ── */}
                    {bookingState === 'error' && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                            <p className="text-sm text-red-700">{bookingError}</p>
                        </div>
                    )}

                    {/* ── Action Buttons ── */}
                    <div className="flex justify-end gap-3">
                        {isEditing ? (
                            <>
                                <button 
                                    onClick={() => setIsEditing(false)} 
                                    className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-200 rounded transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleSave} 
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold shadow-sm transition-colors disabled:opacity-50"
                                >
                                    {isSaving ? 'Saving...' : <><Save size={18} /> Save Changes</>}
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={onClose} className="px-4 py-2 text-gray-600 font-medium hover:text-gray-800 transition-colors">Close</button>
                                {!isCustomerLinked ? (
                                    <button onClick={() => onConvertCustomer(request)} disabled={isConverting} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium disabled:opacity-50 transition-colors shadow-sm">
                                        <UserPlus size={18} /> {isConverting ? 'Converting...' : 'Promote to Customer'}
                                    </button>
                                ) : (
                                    <>
                                        <button onClick={() => onCreateQuote(request)} className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 font-medium transition-colors shadow-sm">
                                            <FileText size={18} /> Create {docWord}
                                        </button>
                                        {request.templateId && onBookNow && bookingState === 'idle' && (
                                            <button
                                                onClick={() => setBookingState('confirming')}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold transition-colors shadow-sm"
                                            >
                                                <FileText size={18} /> Book Now
                                            </button>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequestDetailModal;